import * as Yup from 'yup'
import moment from 'moment'
import { useFormik } from 'formik'
import { useEffect, useRef, useState } from 'react'

import { VesselForecastAPI } from '~/api'
import {
  Lookup,
  Actions,
  insertLog,
  ValidateDateString,
  ConvertLocalTimeGet,
  ConvertLocalTimePost,
  GetDurationInMinutes,
} from '~/utils'

import Helper from './generate-sr-modal.helper'

const useHooks = (props) => {
  const ref = useRef('upload_file')

  const draftValidation = {
    location_id: Yup.string().required('This field can`t be empty'),
    estimation_start_date: Yup.date().required('This field can`t be empty'),
    estimation_finish_date: Yup.date()
      .required('This field can`t be empty')
      .test('estimation_start_date', 'Must not equals & greater than Estimation Start Date', (value) => {
        if (!value || !form.values.estimation_start_date) return false

        const endTime = moment(value).format('DD MMM YYYY HH:mm')
        const startTime = moment(form.values.estimation_start_date).format('DD MMM YYYY HH:mm')
        const duration = moment
          .duration(moment(endTime, 'DD MMM YYYY HH:mm')
            .diff(moment(startTime, 'DD MMM YYYY HH:mm')))
          .asMinutes()

        return duration > 0 ? true : false
      })
  }

  const submitValidation = {
    sr_model_code: Yup.string().required('This field is required'),
    priority_code: Yup.string().required('This field is required'),
    estimation_start_date: Yup.date().required('This field can`t be empty'),
    estimation_finish_date: Yup.date()
      .required('This field can`t be empty')
      .test('estimation_start_date', 'Must not equals & greater than Estimation Start Date', (value) => {
        if (!value || !form.values.estimation_start_date) return false

        const endTime = moment(value).format('DD MMM YYYY HH:mm')
        const startTime = moment(form.values.estimation_start_date).format('DD MMM YYYY HH:mm')
        const duration = moment
          .duration(moment(endTime, 'DD MMM YYYY HH:mm')
            .diff(moment(startTime, 'DD MMM YYYY HH:mm')))
          .asMinutes()

        return duration > 0 ? true : false
      }),
    remarks: Yup.string()
      .required('This field can`t be empty')
      .min(10, 'Must be at least 10 characters')
      .test('remarks', 'Must be at least 10 characters', (value) => {
        if (value) {
          return value.trim().replace(/ +/g, '').length >= 10 ? true : false
        } else {
          return false
        }
      }),
  }

  const [showAlert, setAlert] = useState({ visible: false, msg: '', success: false })
  const [fileSize, setFileSize] = useState(0)
  const [fileTypes, setFileTypes] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [validation, setValidation] = useState('draft')
  const [attachments, setAttachments] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [openConfirmation, setConfirmation] = useState(false)
  const [listPriorityRequest, setListPriorityRequest] = useState([])
  const [listServiceRequestModel, setListServiceRequestModel] = useState([])

  // Utility function to extract and transform enum data
  const extractEnumData = (response, { single = false, splitter = null, mapper = null }) => {
    if (response.status === 200 && response.message?.data?.length > 0) {
      const data = response.message.data
      if (single) return data[0].value
      if (splitter) return data[0].value.split(splitter)
      if (mapper) return data.map(mapper)
    }
    return null
  }

  const fetchAllData = async () => {
    setIsLoading(true)
    try {
      const [fileSizeResponse, fileTypeResponse, priorityRequestResponse, serviceRequestModelResponse] = await Promise.all([
        VesselForecastAPI.GetMasterDataEnum(Helper.PayloadEnumFileSize),
        VesselForecastAPI.GetMasterDataEnum(Helper.PayloadEnumFileType),
        VesselForecastAPI.GetMasterDataEnum(Helper.PayloadEnumPriorityRequest),
        VesselForecastAPI.GetMasterDataEnum(Helper.PayloadEnumServiceRequestModel),
      ])

      const fileSizeData = extractEnumData(fileSizeResponse, { single: true })
      if (fileSizeData) setFileSize(fileSizeData)

      const fileTypeData = extractEnumData(fileTypeResponse, { splitter: '|' })
      if (fileTypeData) setFileTypes(fileTypeData)

      const priorityData = extractEnumData(priorityRequestResponse, {
        mapper: item => ({ label: item.value, value: item.code }),
      })
      if (priorityData) setListPriorityRequest(priorityData)

      const serviceRequestData = extractEnumData(serviceRequestModelResponse, {
        mapper: item => ({ label: item.value, value: item.code }),
      })
      if (serviceRequestData) {
        setListServiceRequestModel(serviceRequestData)

        // find and set the default service request model code to external if exists
        const findExternal = serviceRequestData.find(item => /external/i.test(item.label))
        if (findExternal) {
          form.setFieldValue('sr_model_code', findExternal.value)
        }
      }
    } catch (error) {
      insertLog({
        message: `Error fetching data: ${error}`,
        action: Lookup.LOG_ACTION.UPLOAD,
        module: Lookup.LOG_MODULE.VESSEL_FORECAST,
        submodule: Lookup.LOG_SUB_MODULE.SERVICE_REQUEST,
        refNo: form.values.sr_no || 'SRXXXXXXXXXX',
        dbDestination: 'SR_MAIN'
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchAllData()
  }, [])

  const handleSubmit = async (sr_status) => {
    form.setFieldValue('sr_status', sr_status)
    setValidation(sr_status) // update validation schema mode

    // Wait for Formik to pick up the schema change
    await new Promise((resolve) => setTimeout(resolve, 0))

    const errors = await form.validateForm()
    const touched = Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})

    form.setTouched(touched)

    if (Object.keys(errors).length === 0) {
      if (sr_status === 'confirm') {
        setConfirmation(true) // open confirmation modal
      }

      if (sr_status === 'draft' || sr_status === 'submitted') {
        form.submitForm() // submit form right away
      }
    }
  }

  const handleUpdateVesselForecast = async (sr_code) => {
    try {
      const payload = {
        id: props.dataItem.id,
        sr_code: sr_code,
        product: props.dataItem.product,
        quantity: props.dataItem.quantity,
        uom_code: props.dataItem.uom_code,
        all_fast: props.dataItem.all_fast_non_formatted,
        cast_off: props.dataItem.cast_off_non_formatted,
        is_active: true,
        plan_code: props.dataItem.plan_code,
        percentage: 0,
        jetty_code: props.dataItem.jetty_code,
        laycan_end: props.dataItem.laycan_end,
        vessel_code: props.dataItem.vessel_code,
        client_code: props.dataItem.client_code,
        laycan_start: props.dataItem.laycan_start,
        activity_code: props.dataItem.activity_code,
      }

      const response = await VesselForecastAPI.UpdateVesselForecast(payload)
      return response
    } catch (error) {
      return error
    }
  }

  const handleSubmitForm = async (values) =>  {
    setIsLoading(true)
    try {
      const location = props.dataItem.location_description || ''
      const srModel = listServiceRequestModel.find(item => item.value === values.sr_model_code)
      const srAttachment = attachments
        .filter(item => (item.is_deleted === false || item.id))
        .map(item => {
          const updateData = {
            path: item.path,
            filename: item.filename,
            filesize: item.filesize || 0,
            is_deleted: item.is_deleted || false,
            created_date: ConvertLocalTimePost(props.dataItem.location_description, item.created_date),
          }

          if (item.new) {
            updateData.id = item.id
          }

          return updateData
        })

      const payload = {
        remarks: values.remarks,
        sr_model: srModel?.label || props.dataItem.sr_model || '',
        sr_status: values.sr_status,
        jetty_code: props.dataItem.jetty_code,
        vessel_code: props.dataItem.vessel_code,
        Client_Code: values.client_code,
        Location_Id: values.location_id,
        sr_attachment: srAttachment || [],
        sr_model_code: values.sr_model_code,
        Priority_Code: values.priority_code,
        planning_start_time: props.dataItem.all_fast_non_formatted,
        planning_finish_time: props.dataItem.cast_off_non_formatted,
        Estimate_Start_Time: ConvertLocalTimePost(location, values.estimation_start_date),
        Estimate_Finish_Time: ConvertLocalTimePost(location, values.estimation_finish_date),
        Estimate_Duration_Time: GetDurationInMinutes(values.estimation_start_date, values.estimation_finish_date, 0),
      }

      const response = await VesselForecastAPI.PostGenerateServiceRequest(payload)

      if (response.status === 200) {
        props.toggleAlert({
          msg: 'Service Request has been successfully Generated.',
          type: 'success',
          visible: true,
          success: true,
        })
        form.resetForm()
        localStorage.removeItem('uploads')

        // setup data for updating vessel forecast
        const sr_code = response.data.message.data.code
        const responseUpdateVesselForecast = await handleUpdateVesselForecast(sr_code)
        if (responseUpdateVesselForecast.status !== 200) {
          insertLog({
            message: responseUpdateVesselForecast.message || 'Failed to Submit Vessel Forecast',
            action: Lookup.LOG_ACTION.SUBMIT,
            refNo: form.values.sr_no || 'SRXXXXXXXXXX',
            module: Lookup.LOG_MODULE.VESSEL_FORECAST,
            submodule: Lookup.LOG_SUB_MODULE.SERVICE_REQUEST,
            dbDestination: 'SR_MAIN',
          })
          throw new Error(responseUpdateVesselForecast.message || 'Failed to Update Vessel Forecast')
        }

        setTimeout(() => {
          setIsLoading(false)
          props.toggleModal()
        }, 1000)
      } else {
        throw new Error(response.message || 'Failed to Generate Service Request')
      }
    } catch (error) {
      insertLog({
        message: error,
        action: Lookup.LOG_ACTION.SUBMIT,
        refNo: form.values.sr_no || 'SRXXXXXXXXXX',
        module: Lookup.LOG_MODULE.VESSEL_FORECAST,
        submodule: Lookup.LOG_SUB_MODULE.SERVICE_REQUEST,
        dbDestination: 'SR_MAIN',
      })
      props.toggleAlert({
        msg: `${error}` || 'Failed to Generate Service Request',
        type: 'error',
        visible: true,
        success: false,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmSubmit = () => {
    setConfirmation(false)
    form.setFieldValue('sr_status', 'submitted')

    setTimeout(() => form.submitForm(), 500)
  }

  const handleValidateDateValue = (date, location) => {
    if (ValidateDateString(date)) {
      return ConvertLocalTimeGet(location, date, 'MM/DD/YYYY HH:mm')
    } else {
      return null
    }
  }

  const form = useFormik({
    initialValues: Helper.FormInitialValues,
    validationSchema: Yup.object(validation === 'draft' ? draftValidation : submitValidation),
    onSubmit: (values) => handleSubmitForm(values),
  })

  useEffect(() => {
    if (props) {
      const location = props.dataItem.location_description || ''
      const estimationStartDate = handleValidateDateValue(props.dataItem.laycan_start, location)
      const estimationFinishDate = handleValidateDateValue(props.dataItem.laycan_end, location)

      form.setFieldValue('sr_no', props.dataItem.sr_no || '')
      form.setFieldValue('location', props.dataItem.location_description || '')
      form.setFieldValue('sr_status', props.dataItem.sr_status || '')
      form.setFieldValue('location_id', props.dataItem.location_id || '')
      form.setFieldValue('client_code', props.dataItem.client_code || '')
      form.setFieldValue('customer_name', props.dataItem.customer_name || '')
      form.setFieldValue('estimation_start_date', estimationStartDate ? new Date(estimationStartDate) : '')
      form.setFieldValue('estimation_finish_date', estimationStartDate ? new Date(estimationFinishDate) : '')
    }
  }, [props])

  const handleCloseAlert = () => setAlert({ msg: '', success: false, visible: false })

  const toggleConfirmation = () => setConfirmation(!openConfirmation)

  const handleChangeDropdown = (field, value) => form.setFieldValue(field, value ? value.value : '')

  const handleUpload = async (e) => {
    setIsUploading(true)
    try {
      const files = e.target.files
      const formData = new FormData()
      const acceptedFiles = []

      for (var i = 0; i < files.length; i++) {
        const file_type = files[i].name.split('.')
        if (fileTypes.includes(file_type[1].toLowerCase())) {
          if (files[i].size > fileSize * 1024 * 1024) {
            insertLog({
              message: `One of the file size exceed ${fileSize} Mb`,
              action: Lookup.LOG_ACTION.UPLOAD,
              module: Lookup.LOG_MODULE.VESSEL_FORECAST,
              submodule: Lookup.LOG_SUB_MODULE.SERVICE_REQUEST,
              refNo: form.values.sr_no || 'SRXXXXXXXXXX',
              dbDestination: 'SR_MAIN'
            })
          } else {
            formData.append('file', files[i])
            acceptedFiles.push({ name: files[i].name, type: files[i].name.split('.')[1] })
          }
        } else {
          insertLog({
            message: `You can only upload file with extention ${fileTypes.join(', ')}`,
            action: Lookup.LOG_ACTION.UPLOAD,
            module: Lookup.LOG_MODULE.VESSEL_FORECAST,
            submodule: Lookup.LOG_SUB_MODULE.SERVICE_REQUEST,
            refNo: form.values.sr_no || 'SRXXXXXXXXXX',
            dbDestination: 'SR_MAIN'
          })
          setAlert({
            msg: `You can only upload file with extention ${fileTypes.join(', ')}`,
            visible: true,
            success: false,
          })
        }
      }

      if (formData.getAll('file').length > 0) {
        formData.append('action', Actions.MULTIPLE_UPLOAD)
        const response = await VesselForecastAPI.UploadAttachment(formData)

        if (response.data && response.data.message.data.length > 0) {
          ref.current.value = '' // Reset form input value
          const newAttachment = response.data.message.data.map(item => {
            const data = {
              ...item,
              new: true,
              is_deleted: false,
              mime: item.filename.split('.')[1],
              created_date: new Date().toISOString()
            }
            return data
          })

          setAttachments([...attachments, ...newAttachment])
          localStorage.setItem('uploads', JSON.stringify([...attachments, ...newAttachment]))
        } else {
          setAlert({
            msg: 'Oops! Something went wrong. Please try again later.',
            visible: true,
            success: false,
          })
        }
      }
    } catch (error) {
      insertLog({
        message: `Error uploading document: ${error}`,
        action: Lookup.LOG_ACTION.UPLOAD,
        module: Lookup.LOG_MODULE.VESSEL_FORECAST,
        submodule: Lookup.LOG_SUB_MODULE.SERVICE_REQUEST,
        refNo: form.values.sr_no || 'SRXXXXXXXXXX',
        dbDestination: 'SR_MAIN'
      })
    } finally {
      setIsUploading(false)
    }
  }

  const triggerUpload = () => {
    if (ref.current !== undefined && ref.current.click !== undefined) {
      ref.current.click()
    }
  }

  const removeAttachment = (data) => {
    ref.current.value = '' // Reset form input value
    const updateAttachments = attachments.map(item => item.filename === data.filename ? ({ ...item, is_deleted: true }) : item)
    setAttachments(updateAttachments)
  }

  const handleCloseForm = () => {
    form.resetForm()
    setAttachments([])
    localStorage.removeItem('uploads')
    props.toggleModal()
  }

  return {
    data: {
      ref,
      form,
      fileSize,
      fileTypes,
      isLoading,
      showAlert,
      attachments,
      isUploading,
      openConfirmation,
      listPriorityRequest,
      listServiceRequestModel,
    },
    methods: {
      handleSubmit,
      handleUpload,
      triggerUpload,
      handleCloseForm,
      handleCloseAlert,
      removeAttachment,
      toggleConfirmation,
      handleConfirmSubmit,
      handleChangeDropdown,
    }
  }
}

export default useHooks